import { useCallback } from 'react';
import { Intent } from '@blueprintjs/core';
import {
  ace,
  comment,
  customer,
  loan_application,
} from '@axo/shared/data-access/types';
import { useCreateCallOrder } from '@axo/shared/data-access/hooks/services/ace/useCreateCallOrder';
import { useCreateCallRecord } from '@axo/shared/data-access/hooks/services/ace/useCreateCallRecord';
import { MarketCountryCode } from '@axo/shared/types';
import { AppToaster } from '../../AppToaster';
import { ICommentsState } from './useCommentsState';

interface UseCallActionsArgs {
  applicationID: string;
  application: loan_application.LoanApplication | undefined;
  customer: customer.Customer | undefined;
  bucketName: string;
  internationalNumber: string | undefined;
  outboundNumberType: ace.OutboundNumberType;
  clientID: string;
  hasLock: boolean;
  commentsState: ICommentsState;
}

const showToast = (intent: Intent, message: string) =>
  AppToaster.then((toaster) => toaster.show({ intent, message }));

export function useCallActions({
  applicationID,
  application,
  customer,
  bucketName,
  internationalNumber,
  outboundNumberType,
  clientID,
  hasLock,
  commentsState,
}: UseCallActionsArgs) {
  const { mutate: createCallOrder, isLoading: isCreatingOrder } =
    useCreateCallOrder();
  const { mutate: createCallRecord, isLoading: isCreatingRecord } =
    useCreateCallRecord();

  const isPending = isCreatingOrder || isCreatingRecord;

  const onClickCallTag = useCallback(
    (tag: ace.CallTag) => {
      // Guard against rapid double-clicks: the menu's `disabled` only takes
      // effect after a re-render, so a second click can land before then.
      if (isPending) return;
      if (!application || !customer) return;
      if (!hasLock) {
        showToast('warning', 'You no longer hold the application lock');
        return;
      }

      const status = application.Status;
      const customerPhoneNumber = customer.MobilePhoneNumber;

      if (!internationalNumber) {
        showToast(
          'danger',
          'Failed to start call: The customer has an invalid phone number'
        );
        return;
      }

      if (!status || !customerPhoneNumber) return;

      createCallOrder(
        {
          MarketCountry: application.MarketCountry as MarketCountryCode,
          Tags: [tag.ID],
          ClientID: clientID,
          LoanApplicationID: applicationID,
          LoanApplicationStatus: status,
          BucketName: bucketName,
          CustomerPhoneNumber: internationalNumber,
          OutboundNumberType: outboundNumberType,
        },
        {
          onSuccess: (data) => {
            // 429 from `send` resolves with `undefined`; the hook captures it
            // to Sentry. Tell the agent the request was rate-limited so they
            // know to retry, and skip the misleading "Call initiated" toast.
            if (!data) {
              showToast(
                'warning',
                'Call request was rate-limited — please try again'
              );
              return;
            }
            showToast('primary', 'Call initiated, please check Puzzel');
          },
          onError: (error) => {
            const message =
              error instanceof Error ? error.message : 'Unknown error';
            showToast('danger', `Failed to start call: ${message}`);
          },
        }
      );
    },
    [
      application,
      customer,
      bucketName,
      internationalNumber,
      outboundNumberType,
      clientID,
      applicationID,
      hasLock,
      isPending,
      createCallOrder,
    ]
  );

  const onClickNoCallTag = useCallback(
    (tag: ace.CallTag) => {
      if (isPending) return;
      if (!application || !customer) return;
      if (!hasLock) {
        showToast('warning', 'You no longer hold the application lock');
        return;
      }

      const status = application.Status;
      const customerPhoneNumber = customer.MobilePhoneNumber;

      if (!internationalNumber) {
        showToast('danger', 'The customer has an invalid phone number');
        return;
      }

      if (!status || !customerPhoneNumber) return;

      createCallRecord(
        {
          MarketCountry: application.MarketCountry as MarketCountryCode,
          Tags: [tag.ID],
          ClientID: clientID,
          LoanApplicationID: applicationID,
          LoanApplicationStatus: status,
          BucketName: bucketName,
          CustomerPhoneNumber: internationalNumber,
          Outbound: tag.Name !== 'Inbound',
        },
        {
          onSuccess: (data) => {
            if (!data) {
              showToast(
                'warning',
                'Call record request was rate-limited — please try again'
              );
            }
          },
          onError: (error) => {
            const message =
              error instanceof Error ? error.message : 'Unknown error';
            showToast('danger', `Failed to record call: ${message}`);
          },
        }
      );
    },
    [
      application,
      customer,
      bucketName,
      internationalNumber,
      clientID,
      applicationID,
      hasLock,
      isPending,
      createCallRecord,
    ]
  );

  const onClickCommentTemplate = useCallback(
    (template: comment.CommentTemplate) => {
      commentsState.addNewComment(template);
    },
    [commentsState]
  );

  return {
    onClickCallTag,
    onClickNoCallTag,
    onClickCommentTemplate,
    isPending,
  };
}
